/**
 * A series' page (`/series/:seriesId/:nameSlug?`, shared-client-screens D5, Decision A): the app's
 * own SeriesDetailScreen fed the website's series read, on a phone and, in its centered column, on
 * a computer. A series the studio does not list shows the studio's not-found page.
 *
 * The website's slots: Back returns where the visitor came from on this site, or to the studio's
 * home; Book This Series carries the series to the checkout, which signs in and holds the spot;
 * a signed-in client's View Booking opens their booking page. A failed read says the app's
 * "Something went wrong" with Try again, never the server's own words.
 */
import { useParams } from 'react-router-dom';

import { SeriesDetailScreen } from '@/pages/SeriesDetailPage/SeriesDetailScreen';

import { SiteFetchError, useSiteSeries } from '../api/site';
import { usePageMover, useSiteBack } from '../pageMover';
import { usePageTitle } from '../ui/usePageTitle';
import { NotFoundPage } from './NotFoundPage';

export function SeriesPage() {
  const { seriesId } = useParams<{ seriesId: string }>();
  const navigate = usePageMover();
  const onBack = useSiteBack();
  const query = useSiteSeries(seriesId);
  const series = query.data;
  usePageTitle(series?.title);
  if (query.error instanceof SiteFetchError && query.error.status === 404) return <NotFoundPage />;

  return (
    <SeriesDetailScreen
      status={series ? 'ready' : query.isError ? 'error' : 'loading'}
      series={
        series && {
          title: series.title,
          imageUrl: series.photo?.url ?? null,
          focalPointX: series.photo?.focalX ?? null,
          focalPointY: series.photo?.focalY ?? null,
          focalPointScale: series.photo?.focalScale ?? null,
          dates: series.dates,
          durationMinutes: series.durationMinutes,
          totalPrice: series.price.kind === 'FREE' ? null : series.price.amount,
          spotsRemaining: series.spotsLeft,
          isBookable: series.isBookable,
          location: series.location,
          practitioner: { name: series.practitioner.name, photoUrl: series.practitioner.photoUrl, specialty: series.practitioner.role },
          description: series.description,
          userBooking: series.userBooking,
        }
      }
      onRetry={() => void query.refetch()}
      onBack={onBack}
      onViewBooking={(id) => navigate(`/account/bookings/${encodeURIComponent(id)}`)}
      reserve={{
        onReserve: () => series && navigate(`/checkout?${new URLSearchParams({ series: series.id })}`),
        reserving: false,
        enabled: true,
      }}
    />
  );
}
